// Formulaires de l'écran des actions sociales (lot L7, M20) : activation par le maire ou président, programme
// avec ses critères (aucune règle officielle préremplie), enregistrement d'une demande au guichet. Fenêtres et
// champs de la charte existante ; le serveur refait chaque contrôle.
import type { CritereSocial, ProgrammeSocial } from '@cc/contrats';
import { TYPES_AIDE_SOCIALE } from '@cc/contrats';
import { Bouton, CaseACocher, Champ, Selection, ZoneTexte } from '@cc/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAction } from '../../commun/useAction';
import { Fenetre } from '../achats/Fenetre';
import { creerDemande, creerProgramme, INVALIDER_SOCIAL, modifierActivation } from './api';
import styles from '../achats/achats.module.css';

export function FormulaireActivation({
  actif,
  onFermer,
}: {
  actif: boolean;
  onFermer: () => void;
}) {
  const [motif, setMotif] = useState('');
  const action = useAction({
    executer: () => modifierActivation(!actif, motif),
    succes: actif
      ? "Module désactivé : aucune donnée n'est supprimée."
      : 'Module des actions sociales activé.',
    invalider: [...INVALIDER_SOCIAL, ['contexte'], ['modules']],
    apres: onFermer,
  });
  return (
    <Fenetre
      titre={actif ? 'Désactiver les actions sociales' : 'Activer les actions sociales'}
      description={
        actif
          ? 'La désactivation refuse les nouvelles demandes ; les dossiers existants restent consultables et traités sous habilitation.'
          : 'Activation explicite par le maire ou président, journalisée avec son motif. Les agents devront aussi recevoir une habilitation nominative.'
      }
      libelle={actif ? 'Désactiver' : 'Activer'}
      enCours={action.enCours}
      onFermer={onFermer}
      onValider={() => action.lancer(undefined)}
      erreur={action.messageErreur}
    >
      <ZoneTexte
        libelle="Motif"
        value={motif}
        onChange={(e) => setMotif(e.target.value)}
        required
      />
    </Fenetre>
  );
}

const critereVide = (): CritereSocial => ({
  code: '',
  libelle: '',
  nature: 'declaration',
  obligatoire: true,
});

export function EditeurCriteres({
  criteres,
  onChange,
}: {
  criteres: CritereSocial[];
  onChange: (c: CritereSocial[]) => void;
}) {
  const maj = (i: number, v: Partial<CritereSocial>) =>
    onChange(criteres.map((c, k) => (k === i ? { ...c, ...v } : c)));
  return (
    <fieldset className={styles.bloc}>
      <legend>Critères (proposés à l&apos;instruction, jamais appliqués automatiquement)</legend>
      {criteres.map((c, i) => (
        <div key={i} className={styles.grille}>
          <Champ
            libelle={`Code du critère ${i + 1}`}
            value={c.code}
            onChange={(e) => maj(i, { code: e.target.value })}
            required
          />
          <Champ
            libelle={`Libellé du critère ${i + 1}`}
            value={c.libelle}
            onChange={(e) => maj(i, { libelle: e.target.value })}
            required
          />
          <Selection
            libelle={`Nature du critère ${i + 1}`}
            options={[
              { valeur: 'declaration', libelle: 'Déclaration' },
              { valeur: 'piece', libelle: 'Pièce justificative' },
              { valeur: 'constat', libelle: "Constat de l'agent" },
            ]}
            value={c.nature}
            onChange={(e) => maj(i, { nature: e.target.value as CritereSocial['nature'] })}
          />
          <CaseACocher
            libelle="Obligatoire"
            checked={c.obligatoire}
            onChange={(e) => maj(i, { obligatoire: e.target.checked })}
          />
        </div>
      ))}
      <Bouton variante="secondaire" onClick={() => onChange([...criteres, critereVide()])}>
        Ajouter un critère
      </Bouton>
    </fieldset>
  );
}

export function FormulaireProgramme({ onFermer }: { onFermer: () => void }) {
  const [v, setV] = useState({
    code: '',
    intitule: '',
    objet: '',
    type_aide: 'financiere',
    debut: '',
    fin: '',
    enveloppe: '',
    reference_acte: '',
  });
  const [criteres, setCriteres] = useState<CritereSocial[]>([critereVide()]);
  const action = useAction({
    executer: () =>
      creerProgramme({
        code: v.code,
        intitule: v.intitule,
        objet: v.objet,
        type_aide: v.type_aide as 'financiere',
        debut: v.debut,
        fin: v.fin || undefined,
        enveloppe_indicative: v.enveloppe ? Number(v.enveloppe) : undefined,
        reference_acte: v.reference_acte,
        criteres,
        fictif: false,
      }),
    succes: 'Programme créé en préparation, critères en version 1.',
    invalider: INVALIDER_SOCIAL,
    apres: onFermer,
  });
  return (
    <Fenetre
      titre="Nouveau programme"
      description="Aucune règle officielle ni montant n'est prérempli : la source administrative est exigée. Le programme s'ouvre ensuite aux demandes."
      libelle="Créer le programme"
      enCours={action.enCours}
      onFermer={onFermer}
      onValider={() => action.lancer(undefined)}
      erreur={action.messageErreur}
    >
      <Champ
        libelle="Code"
        value={v.code}
        onChange={(e) => setV((x) => ({ ...x, code: e.target.value }))}
        required
      />
      <Champ
        libelle="Intitulé"
        value={v.intitule}
        onChange={(e) => setV((x) => ({ ...x, intitule: e.target.value }))}
        required
      />
      <ZoneTexte
        libelle="Objet"
        value={v.objet}
        onChange={(e) => setV((x) => ({ ...x, objet: e.target.value }))}
        required
      />
      <Selection
        libelle="Type d'aide"
        options={Object.entries(TYPES_AIDE_SOCIALE).map(([valeur, libelle]) => ({
          valeur,
          libelle,
        }))}
        value={v.type_aide}
        onChange={(e) => setV((x) => ({ ...x, type_aide: e.target.value }))}
      />
      <Champ
        libelle="Début"
        type="date"
        value={v.debut}
        onChange={(e) => setV((x) => ({ ...x, debut: e.target.value }))}
        required
      />
      <Champ
        libelle="Fin"
        type="date"
        value={v.fin}
        onChange={(e) => setV((x) => ({ ...x, fin: e.target.value }))}
        facultatif
      />
      <Champ
        libelle="Enveloppe indicative (FCFA)"
        inputMode="numeric"
        value={v.enveloppe}
        onChange={(e) => setV((x) => ({ ...x, enveloppe: e.target.value }))}
        facultatif
      />
      <Champ
        libelle="Source administrative (délibération, arrêté)"
        value={v.reference_acte}
        onChange={(e) => setV((x) => ({ ...x, reference_acte: e.target.value }))}
        required
      />
      <EditeurCriteres criteres={criteres} onChange={setCriteres} />
    </Fenetre>
  );
}

export function FormulaireDemande({
  programmes,
  onFermer,
}: {
  programmes: ProgrammeSocial[];
  onFermer: () => void;
}) {
  const navigate = useNavigate();
  const [v, setV] = useState({
    programme: '',
    nom: '',
    prenoms: '',
    naissance: '',
    telephone: '',
    quartier: '',
    usager: '',
    situation: '',
    montant: '',
  });
  const action = useAction({
    executer: () =>
      creerDemande({
        programme_id: v.programme,
        beneficiaire: {
          nom: v.nom,
          prenoms: v.prenoms || undefined,
          date_naissance: v.naissance || undefined,
          telephone: v.telephone || undefined,
          quartier: v.quartier || undefined,
          identifiant_usager: v.usager || undefined,
        },
        situation: v.situation,
        montant_demande: v.montant ? Number(v.montant) : undefined,
      }),
    succes: (r) =>
      r.doublons > 0
        ? `Demande ${r.numero} enregistrée ; ${r.doublons} doublon(s) potentiel(s) à examiner.`
        : `Demande ${r.numero} enregistrée.`,
    invalider: INVALIDER_SOCIAL,
    apres: (r) => {
      onFermer();
      void navigate(`/social/demandes/${r.id}`);
    },
  });
  return (
    <Fenetre
      titre="Nouvelle demande"
      description="Enregistrement au guichet. Un doublon potentiel est signalé pour examen par un agent ; il ne bloque ni ne refuse la demande."
      libelle="Enregistrer"
      enCours={action.enCours}
      onFermer={onFermer}
      onValider={() => action.lancer(undefined)}
      erreur={action.messageErreur}
    >
      <Selection
        libelle="Programme"
        options={programmes.map((p) => ({ valeur: p.id, libelle: p.intitule }))}
        optionVide="Choisir un programme ouvert"
        value={v.programme}
        onChange={(e) => setV((x) => ({ ...x, programme: e.target.value }))}
        required
      />
      <Champ
        libelle="Nom"
        value={v.nom}
        onChange={(e) => setV((x) => ({ ...x, nom: e.target.value }))}
        required
      />
      <Champ
        libelle="Prénoms"
        value={v.prenoms}
        onChange={(e) => setV((x) => ({ ...x, prenoms: e.target.value }))}
        facultatif
      />
      <Champ
        libelle="Date de naissance"
        type="date"
        value={v.naissance}
        onChange={(e) => setV((x) => ({ ...x, naissance: e.target.value }))}
        facultatif
      />
      <Champ
        libelle="Téléphone"
        value={v.telephone}
        onChange={(e) => setV((x) => ({ ...x, telephone: e.target.value }))}
        facultatif
      />
      <Champ
        libelle="Quartier"
        value={v.quartier}
        onChange={(e) => setV((x) => ({ ...x, quartier: e.target.value }))}
        facultatif
      />
      <Champ
        libelle="Compte usager du portail (identifiant)"
        value={v.usager}
        onChange={(e) => setV((x) => ({ ...x, usager: e.target.value }))}
        facultatif
        aide="Le demandeur verra l'état de sa demande et le seul message qui lui est destiné."
      />
      <ZoneTexte
        libelle="Situation exposée (confidentielle)"
        value={v.situation}
        onChange={(e) => setV((x) => ({ ...x, situation: e.target.value }))}
        required
      />
      <Champ
        libelle="Montant demandé (FCFA)"
        inputMode="numeric"
        value={v.montant}
        onChange={(e) => setV((x) => ({ ...x, montant: e.target.value }))}
        facultatif
      />
    </Fenetre>
  );
}
